import { createClient } from '@supabase/supabase-js'
import i18n from 'i18next';
import { GOOGLE_MAPS_API_KEY } from './utils/googleMapsApiKey';

const supabase = createClient('https://bpwfkeoiqmliqfskmisj.supabase.co/', 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJwd2ZrZW9pcW1saXFmc2ttaXNqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDU5MjY1MjYsImV4cCI6MjA2MTUwMjUyNn0.cRJz6OASpHtGgDuI8lowEuxUm9KrWR2HGVN9l6MaMwM')

export declare type Country = {
    name: string;
    iso_code: string;
    safety?: 'safe' | 'neutral' | 'unsafe';
}

let countriesCache: Country[] | null = null;

export const UpdateCountriesLanguage = () => {
    if (countriesCache) {
        countriesCache = countriesCache.map(country => ({
            ...country,
            name: i18n.t(`countries.${country.iso_code}`, { defaultValue: country.name })
        }));
    }
};

export const GetCountries: () => Promise<Country[]> = async () => {
    if (countriesCache) return countriesCache;
    const { data, error } = await supabase.from('countries').select('*');
    if (error) {
        console.error('Error fetching countries:', error);
        return [];
    }
    countriesCache = data as Country[];
    UpdateCountriesLanguage();
    return countriesCache;
}

export declare type Disease = {
    name: string;
    description: string;
    required: boolean;
    price: number;
    risk_level: 'low' | 'medium' | 'high';
    side_effects: string[];
    validity: string | null;
}

export const GetCountryDiseases: (iso_code: string, t: (key: string) => string) => Promise<Disease[]> = async (iso_code: string, t: (key: string) => string) => {
    const currentLanguage = i18n.language;
    const { data, error } = await supabase.rpc('get_diseases_by_country_and_language', { my_iso: iso_code, my_lang: currentLanguage });
    if (error) {
        console.error('Error fetching diseases:', error);
        return [];
    }
    const riskLevels = ['low', 'medium', 'high'];
    for (let i = 0; i < data.length; i++) {
        data[i].risk_level = riskLevels[data[i].risk_level ?? 0];
        data[i].side_effects = data[i].side_effects ?? [t('database.noData')];
        if (data[i].validity === "") {
            data[i].validity = t('database.doctorRecommendation');
        } else if (data[i].validity !== null && data[i].validity == 0) {
            data[i].validity = t('database.lessThanYear');
        } else {
            data[i].validity = `${data[i].validity} ${t('database.years')}`;
        }
    }
    return data as Disease[];
};

export interface Clinic {
    id: string;
    name: string;
    address: string;
    city: string;
    postal_code: string;
    latitude: number;
    longitude: number;
    phone_number: string;
    iso_code: string;
    email: string;
    website_url: string;
    distance?: number; // Odległość od wyszukiwanego miejsca w km
}

// Funkcja do obliczania odległości między dwoma punktami (wzór Haversine)
export const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Promień Ziemi w kilometrach
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
        Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c; // Odległość w kilometrach
};

export const GetCountryClinics = async (countryCode: string): Promise<Clinic[]> => {
    try {
        console.log('Fetching clinics for country:', countryCode);
        
        // Najpierw pobierz wszystkie kliniki aby zobaczyć strukturę
        const { data, error } = await supabase
            .from('clinics')
            .select('*');

        if (error) {
            console.error('Error fetching clinics from Supabase:', error);
            return [];
        }

        console.log('All clinics from Supabase:', data);
        
        if (data && data.length > 0) {
            // Sprawdź jakie kolumny są dostępne
            const firstClinic = data[0];
            console.log('First clinic structure:', firstClinic);
            console.log('Available columns:', Object.keys(firstClinic));
            
            // Jeśli nie ma kolumny iso_code, spróbuj pobrać wszystkie kliniki
            // i filtrować po kraju na podstawie miasta lub innej kolumny
            let filteredClinics = data;
            
            // Sprawdź czy istnieje kolumna iso_code
            if ('iso_code' in firstClinic) {
                filteredClinics = data.filter((clinic: any) => clinic.iso_code === countryCode);
            } else if ('country' in firstClinic) {
                // Jeśli jest kolumna country zamiast iso_code
                filteredClinics = data.filter((clinic: any) => clinic.country === countryCode);
            } else {
                // Jeśli nie ma kolumny kraju, zwróć wszystkie kliniki
                console.log('No country column found, returning all clinics');
                filteredClinics = data;
            }
            
            console.log('Filtered clinics for country:', countryCode, filteredClinics);
            
            // Sprawdź czy dane mają wszystkie wymagane pola
            const mappedClinics = filteredClinics.map((clinic: any) => {
                // Mapuj nazwy kolumn jeśli są inne niż oczekiwane
                const mappedClinic = {
                    id: clinic.id || clinic.clinic_id,
                    name: clinic.name || clinic.clinic_name,
                    address: clinic.address,
                    city: clinic.city,
                    postal_code: clinic.postal_code || clinic.postalcode,
                    latitude: parseFloat(clinic.latitude) || 0,
                    longitude: parseFloat(clinic.longitude) || 0,
                    phone_number: clinic.phone_number || clinic.phone || clinic.telephone,
                    iso_code: clinic.iso_code || clinic.country_code || 'PL', // domyślnie PL
                    email: clinic.email,
                    website_url: clinic.website_url || clinic.website || clinic.url
                };
                
                console.log('Mapped clinic:', mappedClinic);
                return mappedClinic;
            });
            
            return mappedClinics as Clinic[];
        } else {
            console.log('No clinics found in Supabase');
            return [];
        }
    } catch (error) {
        console.error('Error in GetCountryClinics:', error);
        return [];
    }
};

export const GetCountryCodeFromAddress = async (address: string): Promise<string | null> => {
    try {
        const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${GOOGLE_MAPS_API_KEY}`
        );
        const data = await response.json();

        console.log('Geocoding response for address:', data); // Dodaj to dla debugowania

        if (data.status === 'OK' && data.results.length > 0) {
            // Find the country component in the address components
            const addressComponents = data.results[0].address_components;
            const countryComponent = addressComponents.find(
                (component: any) => component.types.includes('country')
            );

            if (countryComponent) {
                return countryComponent.short_name; // Returns ISO country code (e.g., "PL", "DE")
            }
        } else if (data.status === 'REQUEST_DENIED') {
            console.error("Google Maps API key is invalid or missing required APIs");
        } else if (data.status === 'ZERO_RESULTS') {
            console.log("No results found for this address");
        } else {
            console.log("Error geocoding address:", data.status);
        }
        return null;
    } catch (error) {
        console.error('Error geocoding address:', error);
        return null;
    }
};

export const SearchClinicsByAddress = async (address: string): Promise<{ clinics: Clinic[], searchLocation: { lat: number, lng: number } | null }> => {
    try {
        const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${GOOGLE_MAPS_API_KEY}`
        );
        const data = await response.json();

        console.log('Geocoding response for address:', data);

        if (data.status === 'OK' && data.results.length > 0) {
            const searchLocation = {
                lat: data.results[0].geometry.location.lat,
                lng: data.results[0].geometry.location.lng
            };

            // Find the country component in the address components
            const addressComponents = data.results[0].address_components;
            const countryComponent = addressComponents.find(
                (component: any) => component.types.includes('country')
            );

            if (countryComponent) {
                const countryCode = countryComponent.short_name;
                console.log('Found country code:', countryCode);
                const clinics = await GetCountryClinics(countryCode);
                
                // Oblicz odległości i sortuj kliniki
                const clinicsWithDistance = clinics.map((clinic: Clinic) => ({
                    ...clinic,
                    distance: calculateDistance(
                        searchLocation.lat, 
                        searchLocation.lng, 
                        clinic.latitude, 
                        clinic.longitude
                    )
                })).sort((a: Clinic, b: Clinic) => (a.distance || 0) - (b.distance || 0));

                console.log('Clinics sorted by distance:', clinicsWithDistance);
                return { clinics: clinicsWithDistance, searchLocation };
            }
        } else if (data.status === 'REQUEST_DENIED') {
            console.error("Google Maps API key is invalid or missing required APIs");
        } else if (data.status === 'ZERO_RESULTS') {
            console.log("No results found for this address");
        } else {
            console.log("Error geocoding address:", data.status);
        }
        
        console.log('Could not determine country from address');
        return { clinics: [], searchLocation: null };
    } catch (error) {
        console.error('Error geocoding address:', error);
        return { clinics: [], searchLocation: null };
    }
};